import React from "react"

interface BrandCampaign {
  campaign?: string
  campaignId?: string
  count: number
}

interface BrandBase {
  count: number
  brandName?: string
  campaigns?: BrandCampaign[]
  campaignDetails?: { id: string; name: string }[]
}

type Brand = BrandBase & ({ brand_id: string } | { brand: string })

interface SelectedTransition {
  from: string
  to: string
  brands: Brand[]
  date: string
}

interface BrandDetailsModalProps {
  isOpen: boolean
  selectedTransition: SelectedTransition | null
  onClose: () => void
  getStateDisplayName: (state: string) => string
}

const BrandDetailsModal: React.FC<BrandDetailsModalProps> = ({
  isOpen,
  selectedTransition,
  onClose,
  getStateDisplayName,
}) => {
  if (!isOpen || !selectedTransition) {
    return null
  }

  const formatDate = (dateString: string) => {
    if (Number.isNaN(Date.parse(dateString))) {
      return dateString
    }
    if (dateString.includes(" - ")) {
      return dateString
        .split(" - ")
        .map((date) => {
          const parsedDate = new Date(date)
          return parsedDate.toISOString().split("T")[0]
        })
        .join(" - ")
    }
    return new Date(dateString).toISOString().split("T")[0]
  }

  const getBrandId = (brand: Brand) => ("brand_id" in brand ? brand.brand_id : brand.brand)

  const getCampaignItems = (brand: Brand) => {
    if (!brand.campaigns || brand.campaigns.length === 0) {
      return []
    }
    const campaignNameMap = new Map(
      (brand.campaignDetails || []).map((detail) => [detail.id, detail.name]),
    )
    return brand.campaigns
      .map((campaign) => {
        const id = campaign.campaignId || campaign.campaign || ""
        if (!id) {
          return null
        }
        return {
          id,
          name: campaignNameMap.get(id) || id,
          count: campaign.count,
        }
      })
      .filter(
        (campaign): campaign is { id: string; name: string; count: number } => campaign !== null,
      )
      .sort((a, b) => b.count - a.count)
  }

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-white shadow-xl border-l border-gray-200 z-50 flex flex-col">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex-shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold">Marka Detayları</h3>
          <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors">
            <span className="text-2xl">×</span>
          </button>
        </div>
        <div className="mt-2 text-blue-100">
          <p>
            <span className="font-medium">Durum Değişimi:</span>{" "}
            {getStateDisplayName(selectedTransition.from)} →{" "}
            {getStateDisplayName(selectedTransition.to)}
          </p>
          <p>
            <span className="font-medium">Tarih:</span> {formatDate(selectedTransition.date)}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 min-h-0">
        <div className="mb-4">
          <h4 className="text-lg font-semibold text-gray-900 mb-3">
            Markalar ({selectedTransition.brands.length})
          </h4>
          <div className="space-y-3">
            {selectedTransition.brands.map((brand, index) => {
              const brandId = getBrandId(brand)
              const campaignItems = getCampaignItems(brand)
              return (
                <div key={brandId} className="bg-gray-50 rounded-lg border border-gray-200">
                  <a
                    className="block cursor-pointer p-4"
                    href={`/brands/find/${brandId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{brand.brandName || brandId}</p>
                          <p className="text-sm text-gray-500">{brand.count} başvuru</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-blue-600">
                          {brand.count.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </a>
                  {campaignItems.length > 0 && (
                    <div className="px-4 pb-4">
                      <div className="text-xs font-semibold text-gray-600 mb-2">
                        Kampanyalar ({campaignItems.length})
                      </div>
                      <div className="space-y-2">
                        {campaignItems.map((campaign) => (
                          <a
                            key={campaign.id}
                            href={`/campaigns/find/${campaign.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm hover:bg-blue-100 hover:border-blue-300 transition-colors"
                          >
                            <span className="flex items-center gap-3 text-gray-700">
                              <svg
                                className="h-5 w-5 text-blue-500"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <path d="M10 13a5 5 0 0 1 0-7l1.5-1.5a5 5 0 0 1 7 7L17 12" />
                                <path d="M14 11a5 5 0 0 1 0 7L12.5 19.5a5 5 0 0 1-7-7L7 11" />
                              </svg>
                              <span>{campaign.name}</span>
                            </span>
                            <span className="rounded-full bg-blue-600 px-3 py-1 text-sm font-semibold text-white">
                              {campaign.count.toLocaleString()}
                            </span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex-shrink-0">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Toplam:{" "}
            {selectedTransition.brands
              .reduce((sum, brand) => sum + brand.count, 0)
              .toLocaleString()}{" "}
            başvuru
          </div>
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  )
}

export default BrandDetailsModal
